/**
 * Resmi Tatil servisi.
 *
 * Spec madde 9:
 *  - Sadece BOSS ekleyebilir/silebilir
 *  - Her tarih için katsayı: 'calismaz' | 1 | 1.5 | 2
 *  - Maaş hesabında: vardiya × saatlik × katsayı
 */

import {
  collection, doc, addDoc, deleteDoc, getDoc, getDocs, updateDoc,
  query, where, orderBy,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import type { FsTimestamp, IsoTarih } from '@/types'

const COLL = 'resmiTatiller'

export type TatilKatsayisi = 'calismaz' | 1 | 1.5 | 2

export const KATSAYI_ETIKETLERI: Record<string, string> = {
  'calismaz': 'Çalışmaz',
  '1': 'x1 (normal)',
  '1.5': 'x1.5',
  '2': 'x2',
}

export interface ResmiTatil {
  id: string
  tarih: IsoTarih           // YYYY-MM-DD
  isim: string              // "Cumhuriyet Bayramı"
  katsayi: TatilKatsayisi
  dini: boolean             // dini bayram mı (yıllık değişir)
  olusturanId: string
  olusturmaTarihi: FsTimestamp
}

export interface YeniTatilData {
  tarih: IsoTarih
  isim: string
  katsayi: TatilKatsayisi
  dini?: boolean
}

// ═══════════════════════════════════════════════════════════════
// CRUD
// ═══════════════════════════════════════════════════════════════

export async function tatilEkle(
  data: YeniTatilData,
  olusturanId: string
): Promise<string> {
  // Aynı tarihte tatil var mı kontrol
  const mevcut = await tatilGetirByTarih(data.tarih)
  if (mevcut) {
    throw new Error(`${data.tarih} tarihinde zaten "${mevcut.isim}" tatili var`)
  }

  const db = getFirestoreDb()
  const docData: Record<string, unknown> = {
    tarih: data.tarih,
    isim: data.isim.trim(),
    katsayi: data.katsayi,
    dini: data.dini ?? false,
    olusturanId,
    olusturmaTarihi: nowServer(),
  }
  const ref = await addDoc(collection(db, COLL), docData)
  return ref.id
}

export async function tatilGuncelle(
  tatilId: string,
  data: Partial<YeniTatilData>
): Promise<void> {
  const db = getFirestoreDb()
  const updates: Record<string, unknown> = {}
  if (data.isim !== undefined) updates.isim = data.isim.trim()
  if (data.katsayi !== undefined) updates.katsayi = data.katsayi
  if (data.dini !== undefined) updates.dini = data.dini
  await updateDoc(doc(db, COLL, tatilId), updates)
}

export async function tatilSil(tatilId: string): Promise<void> {
  const db = getFirestoreDb()
  await deleteDoc(doc(db, COLL, tatilId))
}

// ═══════════════════════════════════════════════════════════════
// LİSTELEME
// ═══════════════════════════════════════════════════════════════

export async function tumTatilleriGetir(): Promise<ResmiTatil[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, COLL), orderBy('tarih', 'asc'))
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ResmiTatil, 'id'>) }))
}

export async function yilTatilleriGetir(yil: number): Promise<ResmiTatil[]> {
  const db = getFirestoreDb()
  const baslangic = `${yil}-01-01`
  const bitis = `${yil}-12-31`
  const q = query(
    collection(db, COLL),
    where('tarih', '>=', baslangic),
    where('tarih', '<=', bitis),
    orderBy('tarih', 'asc')
  )
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ResmiTatil, 'id'>) }))
}

export async function tatilGetir(tatilId: string): Promise<ResmiTatil | null> {
  const db = getFirestoreDb()
  const snap = await getDoc(doc(db, COLL, tatilId))
  if (!snap.exists()) return null
  return { id: snap.id, ...(snap.data() as Omit<ResmiTatil, 'id'>) }
}

export async function tatilGetirByTarih(tarih: IsoTarih): Promise<ResmiTatil | null> {
  const db = getFirestoreDb()
  const q = query(collection(db, COLL), where('tarih', '==', tarih))
  const snap = await getDocs(q)
  if (snap.empty) return null
  const d = snap.docs[0]
  return { id: d.id, ...(d.data() as Omit<ResmiTatil, 'id'>) }
}

/**
 * Bir tarih için katsayı (varsa). Maaş hesabında kullanılır.
 * Yoksa null döner = normal gün.
 */
export async function tarihiTatilMi(tarih: IsoTarih): Promise<ResmiTatil | null> {
  return tatilGetirByTarih(tarih)
}

// ═══════════════════════════════════════════════════════════════
// KKTC DEFAULT TATILLERİ (toplu ekleme için)
// ═══════════════════════════════════════════════════════════════

/** Sabit (her yıl aynı gün) KKTC resmi tatilleri */
export const KKTC_SABIT_TATILLER = [
  { ay: 1, gun: 1, isim: 'Yılbaşı' },
  { ay: 4, gun: 23, isim: 'Ulusal Egemenlik ve Çocuk Bayramı' },
  { ay: 5, gun: 1, isim: 'Emek ve Dayanışma Günü' },
  { ay: 5, gun: 19, isim: "Atatürk'ü Anma, Gençlik ve Spor Bayramı" },
  { ay: 7, gun: 20, isim: 'Barış ve Özgürlük Bayramı' },
  { ay: 8, gun: 1, isim: 'TMT Günü' },
  { ay: 8, gun: 30, isim: 'Zafer Bayramı' },
  { ay: 10, gun: 29, isim: 'Cumhuriyet Bayramı' },
  { ay: 11, gun: 15, isim: 'KKTC Cumhuriyeti İlanı' },
]

/** Bir yıl için sabit tatilleri toplu ekle. Varsa atlar, eksikleri ekler. */
export async function kktcSabitTatilleriEkle(
  yil: number,
  olusturanId: string,
  katsayi: TatilKatsayisi = 1.5
): Promise<{ eklenen: number; atlanan: number }> {
  let eklenen = 0
  let atlanan = 0
  for (const t of KKTC_SABIT_TATILLER) {
    const tarih = `${yil}-${String(t.ay).padStart(2, '0')}-${String(t.gun).padStart(2, '0')}` as IsoTarih
    const mevcut = await tatilGetirByTarih(tarih)
    if (mevcut) {
      atlanan++
      continue
    }
    await tatilEkle({ tarih, isim: t.isim, katsayi, dini: false }, olusturanId)
    eklenen++
  }
  return { eklenen, atlanan }
}
